/**
 * Active session card in the Lumyn Live host list.
 *
 * Extracted verbatim from LiveHostPage.jsx during the Phase 2 refactor
 * (no behaviour change).
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { ChevronRight, Users, Pencil, Calendar } from 'lucide-react';
import { formatSessionDateTime, bestSessionTimestamp } from '../../../utils/formatSessionDateTime';
import { RenamePopover } from './RenamePopover';

export function ActiveSessionCard({ session, onSelect, onRenamed }) {
  const [renaming, setRenaming] = useState(false);
  const { label, iso } = bestSessionTimestamp(session);

  return (
    <div className="relative" data-testid={`session-${session.session_id}`}>
      <button
        onClick={() => onSelect(session)}
        className="text-left w-full"
      >
        <Card className="hover:border-[var(--lumyn-live)] hover:shadow-md hover:shadow-[var(--lumyn-live)]/10 transition cursor-pointer">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="flex-1 truncate" data-testid="session-card-title">{session.quiz_title}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {session.status}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1.5 pt-0">
            <div className="flex items-center gap-3">
              <span className="font-mono text-base text-[var(--lumyn-live)] font-semibold">{session.ref_code}</span>
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" />{session.participant_count}
              </span>
              {session.status === 'running' && (
                <span>Q{session.current_question_index + 1}/{session.total_questions}</span>
              )}
              <ChevronRight className="ml-auto w-4 h-4" />
            </div>
            {iso && (
              <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/80" data-testid="session-card-timestamp">
                <Calendar className="w-3 h-3" />
                <span>{label} · {formatSessionDateTime(iso)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </button>
      {/* Pencil button — sits above the card and intercepts clicks */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setRenaming((v) => !v); }}
        className="absolute top-2 right-12 p-1 rounded-md text-muted-foreground hover:text-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/10 transition"
        title="Rename session"
        aria-label="Rename session"
        data-testid="session-rename-btn"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      {renaming && (
        <RenamePopover
          session={session}
          onSaved={(updated) => { setRenaming(false); onRenamed(updated); }}
          onClose={() => setRenaming(false)}
        />
      )}
    </div>
  );
}
